// Failure ceiling for the "Connecting your Bridge" screen (ENG-3796).
//
// Separate from useBridgeSteps on purpose: that hook reports what is true, this
// one decides when waiting has gone on too long. Keeping them apart means the
// indicator cannot be made to lie by a timeout, and the timeout cannot be
// accidentally reset by a re-render of the indicator.

import { useEffect, useState } from 'react';
import { BRIDGE_READY_TIMEOUT_MS } from '../constants.js';

/**
 * Report whether the bridge has failed to become ready in time.
 *
 * @param armed - Whether the clock should be running. False until the funnel
 *   actually reaches the connecting screen, so time spent reading the intro
 *   screens does not count against the budget.
 * @param ready - Whether the bridge is usable. Stops the clock, and un-fires a
 *   timeout that already triggered — a retry that brings the agent list back
 *   must clear the failure state rather than strand the user on it.
 * @param resetKey - Changes to start the wait over. Without it, "Try Again" on
 *   the failure screen re-entered a wait that had ALREADY expired, so the user
 *   was thrown back to the same failure with no wait at all — the button looked
 *   dead (reported live 2026-08-25).
 */
export const useBridgeTimeout = (armed: boolean, ready: boolean, resetKey: number = 0): boolean => {
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!armed || ready) return;

    const timer = setTimeout(() => setTimedOut(true), BRIDGE_READY_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [armed, ready, resetKey]);

  // A new resetKey means the user asked to wait again. Clear the fired timeout
  // BEFORE the clock above restarts, so the failure screen goes away rather
  // than persisting under a running wait.
  useEffect(() => {
    setTimedOut(false);
  }, [resetKey]);

  // Readiness arriving late still counts. Without this a bridge that connects
  // one second after the ceiling leaves the user looking at a failure screen
  // for a bridge that is now working.
  useEffect(() => {
    if (ready) setTimedOut(false);
  }, [ready]);

  return timedOut;
};
